import { useState } from 'react';
import { Button, Dialog, DialogContent, Box, Typography, useTheme, alpha } from '@mui/material';
import { useHandleLogOut } from './useHandleLogOut';
import { AccountSettings } from './AccountSettings';
import { CloseoutSettings } from './CloseoutSettings';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import HelpIcon from '@mui/icons-material/Help';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CloseIcon from '@mui/icons-material/Close';

// Import responsive utilities
import { useIsMobile } from '../../theme';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';

type SettingPanelOptions =
  | 'adminPanel'
  | 'userSettings'
  | 'closeoutSettings'
  | 'accountSettings'
  | 'logout'
  | 'designSystem';

export type SettingsProps = {
  onCloseMenu: () => void;
  onLogout: () => void;
};

export const Settings = ({ onCloseMenu, onLogout }: SettingsProps) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const setIsHelpOpen = useStore(useUnityBuildStore).setIsHelpOpen;
  const isHelpOpen = useStore(useUnityBuildStore).isHelpOpen;

  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
  const [isUserSettingsOpen, setIsUserSettingsOpen] = useState(false);
  const [isCloseoutSettingsOpen, setIsCloseoutSettingsOpen] = useState(false);
  const [isAccountSettingsOpen, setIsAccountSettingsOpen] = useState(false);
  const [isDesignSystemOpen, setIsDesignSystemOpen] = useState(false);

  const handleLogOut = useHandleLogOut({ onCloseMenu, onLogout });

  const HandleOpenSetting = (option: SettingPanelOptions) => {
    switch (option) {
      case 'adminPanel':
        setIsAdminPanelOpen(!isAdminPanelOpen);
        break;
      case 'userSettings':
        setIsUserSettingsOpen(!isUserSettingsOpen);
        break;
      case 'closeoutSettings':
        setIsCloseoutSettingsOpen(!isCloseoutSettingsOpen);
        break;
      case 'accountSettings':
        setIsAccountSettingsOpen(!isAccountSettingsOpen);
        break;
      case 'designSystem':
        setIsDesignSystemOpen(!isDesignSystemOpen);
        break;
      case 'logout':
        handleLogOut();
        break;
      default:
        break;
    }
  };

  const renderDialog = (
    open: boolean,
    onClose: () => void,
    title: string,
    content: React.ReactNode
  ) => (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={`${title.toLowerCase().replace(/\s+/g, '-')}-title`}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
          borderRadius: isMobile ? 0 : 2,
          boxShadow: theme.shadows[4],
          height: isMobile ? '100%' : 'auto',
        },
      }}
    >
      <DialogContent
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          padding: isMobile ? 2 : 3,
          backgroundColor: theme.palette.background.paper,
          height: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            position: isMobile ? 'sticky' : 'relative',
            top: isMobile ? 0 : 'auto',
            zIndex: isMobile ? 10 : 1,
            backgroundColor: theme.palette.background.paper,
            py: isMobile ? 1 : 0,
            boxShadow: isMobile ? 1 : 0,
          }}
        >
          <Typography variant="h5" component="h2" color="text.primary">
            {title}
          </Typography>
          <Button
            onClick={onClose}
            sx={{
              minWidth: 0,
              padding: 1,
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.action.hover, 0.1),
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>
        {content}
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          padding: 2,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1,
          boxShadow: theme.shadows[1],
          minWidth: isMobile ? 'auto' : '200px',
        }}
      >
        <Button
          onClick={() => setIsHelpOpen(!isHelpOpen)}
          startIcon={<HelpIcon />}
          sx={{
            justifyContent: 'flex-start',
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.hover, 0.1),
            },
          }}
        >
          Instructions
        </Button>
        <Button
          onClick={HandleOpenSetting.bind(null, 'closeoutSettings')}
          startIcon={<SettingsIcon />}
          sx={{
            justifyContent: 'flex-start',
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.hover, 0.1),
            },
          }}
        >
          Closeout Settings
        </Button>
        <Button
          onClick={HandleOpenSetting.bind(null, 'accountSettings')}
          startIcon={<AccountCircleIcon />}
          sx={{
            justifyContent: 'flex-start',
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.hover, 0.1),
            },
          }}
        >
          Account Settings
        </Button>
        <Button
          onClick={handleLogOut}
          startIcon={<LogoutIcon />}
          sx={{
            justifyContent: 'flex-start',
            color: theme.palette.error.main,
            '&:hover': {
              backgroundColor: alpha(theme.palette.error.main, 0.1),
            },
          }}
        >
          Log Out
        </Button>
      </Box>

      {renderDialog(
        isCloseoutSettingsOpen,
        HandleOpenSetting.bind(null, 'closeoutSettings'),
        'Closeout Settings',
        <Box sx={{ height: isMobile ? 'auto' : '900px' }}>
          <CloseoutSettings />
        </Box>
      )}

      {renderDialog(
        isAccountSettingsOpen,
        HandleOpenSetting.bind(null, 'accountSettings'),
        'Account Settings',
        <Box sx={{ height: isMobile ? 'auto' : '900px' }}>
          <AccountSettings />
        </Box>
      )}
    </>
  );
};
